import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Timer, Loader2, ClipboardCheck, CalendarDays } from "lucide-react";

function fmtHours(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function formatDate(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function StatTile({ label, value, icon: Icon, testid }) {
  return (
    <div className="card-surface p-4 flex items-start gap-3" data-testid={testid}>
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-clinical-primary/10 text-clinical-primary">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="label-eyebrow">{label}</div>
        <div className="mt-1 font-heading text-2xl font-semibold text-clinical-text">
          {value}
        </div>
      </div>
    </div>
  );
}

export default function MyHours() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/me/hours-summary", { params: { days } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-6" data-testid="my-hours-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Time logged</div>
          <h1 className="mt-1 text-2xl font-heading font-semibold">Hours &amp; pay</h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            Closed clock-in/out shifts only. Currently open shifts aren't counted until you clock out.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-clinical-border bg-white p-1">
          {[7, 14, 30].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setDays(n)}
              data-testid={`hours-range-${n}`}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                days === n
                  ? "bg-clinical-primary text-white"
                  : "text-clinical-subtext hover:bg-clinical-bg"
              }`}
            >
              {n} days
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-clinical-subtext">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading hours…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatTile
              label="Total hours"
              value={(data?.total_hours ?? 0).toFixed(2)}
              icon={Timer}
              testid="stat-total-hours"
            />
            <StatTile
              label="Shifts completed"
              value={data?.total_shifts ?? 0}
              icon={ClipboardCheck}
              testid="stat-total-shifts"
            />
            <StatTile
              label="Days worked"
              value={data?.daily?.length ?? 0}
              icon={CalendarDays}
              testid="stat-days-worked"
            />
          </div>

          <div className="card-surface p-4">
            <h2 className="font-heading text-lg font-medium mb-3">By day</h2>
            {(!data?.daily || data.daily.length === 0) ? (
              <p className="text-sm text-clinical-subtext italic" data-testid="hours-empty">
                No closed shifts in the last {days} days.
              </p>
            ) : (
              <ul className="divide-y divide-clinical-border" data-testid="hours-daily-list">
                {data.daily.map((d) => (
                  <li
                    key={d.date}
                    className="flex items-center justify-between gap-3 py-2.5"
                    data-testid={`hours-day-${d.date}`}
                  >
                    <div className="text-sm text-clinical-text">{formatDate(d.date)}</div>
                    <div className="text-xs text-clinical-subtext">
                      {d.shifts} {d.shifts === 1 ? "shift" : "shifts"}
                    </div>
                    <div className="font-mono text-sm font-medium text-clinical-text">
                      {fmtHours(d.minutes)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {data?.shifts?.length > 0 && (
            <div className="card-surface p-4">
              <h2 className="font-heading text-lg font-medium mb-3">Recent shifts</h2>
              <ul className="divide-y divide-clinical-border" data-testid="hours-shifts-list">
                {data.shifts.slice(0, 20).map((s) => {
                  const inT = s.clock_in_at ? new Date(s.clock_in_at) : null;
                  const outT = s.clock_out_at ? new Date(s.clock_out_at) : null;
                  return (
                    <li
                      key={s.id}
                      className="flex items-center justify-between gap-3 py-2.5"
                      data-testid={`hours-shift-${s.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-clinical-text truncate">
                          {s.client_name}
                        </div>
                        <div className="mt-0.5 text-[11px] text-clinical-subtext">
                          {inT?.toLocaleString([], {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }) || "—"}
                          {outT && (
                            <>
                              {" "}
                              →{" "}
                              {outT.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="font-mono text-sm font-medium text-clinical-text">
                        {fmtHours(s.duration_minutes || 0)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
